import React, { useState } from 'react';
import classnames from 'classnames';
import {
  Control,
  FieldErrors,
  UseFormGetValues,
  useController,
} from 'react-hook-form';
import { CreateSearchFormData } from '../CreateSearchSchema';
import exclamation from '../../../icons/exclamationCircle.svg';
import styles from './providersStep.module.scss';
import { SearchFilterEntity, SEARCH_FILTER_KEYS } from 'v2/models';
import {
  GeneralSelect,
  OptionType,
  SingleValue,
} from 'v2/form/CustomSelectors/GeneralSelect';
import ExcludeProvidersFilter from './ExcludeProvidersFilter';
import ExcludeDataVendorsFilter from './ExcludeDataVendorsFilter';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';

interface ProvidersStepProps {
  filters: SearchFilterEntity[];
  errors: FieldErrors<CreateSearchFormData>;
  control: Control<CreateSearchFormData>;
  getValues: UseFormGetValues<CreateSearchFormData>;
  showModal: boolean;
  onConfirmModal: () => void;
  onCancelModal: () => void;
}

const options: OptionType[] = [
  {
    label: 'Select from my DRM',
    value: SEARCH_FILTER_KEYS.exclude_providers_drm,
  },
  {
    label: 'Add providers manually',
    value: SEARCH_FILTER_KEYS.exclude_providers,
  },
];

const ProvidersStep = ({
  filters,
  control,
  errors,
  getValues,
  showModal,
  onConfirmModal,
  onCancelModal,
}: ProvidersStepProps) => {
  const defaultFilters = getValues('filters');
  const [selectedOption, setSelectedOption] = useState<SingleValue<OptionType>>(
    defaultFilters?.exclude_providers_drm ? options[0] : options[1]
  );

  const {
    field: { value: currentFiltersValue, onChange },
  } = useController<CreateSearchFormData, 'filters'>({
    name: 'filters',
    control,
  });

  const existsFilter = (keyToCheck: string) => {
    return filters.some(
      (filter) => filter.type === 'category' && filter.key === keyToCheck
    );
  };

  const handleChangeExcludeMethod = (option: SingleValue<OptionType>) => {
    const newValue = {
      ...currentFiltersValue,
    };

    if (option?.value === SEARCH_FILTER_KEYS.exclude_providers_drm) {
      newValue.exclude_providers_drm = { is_all: true, list: [] };
      delete newValue.exclude_providers;
    } else if (option?.value === SEARCH_FILTER_KEYS.exclude_providers) {
      newValue.exclude_providers = [];
      delete newValue.exclude_providers_drm;
    }

    onChange(newValue);
    setSelectedOption(option);
  };

  return (
    <div className={styles.headLabel}>
      <div className={styles.messageLabel}>
        <img
          src={exclamation}
          alt={'infoIcon'}
          className={styles.infoIcon}
        ></img>
        <p className={styles.textLabel}>
          Data sellers put a lot of effort into their responses, so to avoid
          unnecessary work please let us know any providers you have already
          discussed this request with or who you do not want notified about this
          particular request.
        </p>
      </div>
      <div className={styles.root}>
        <span className={styles.textForm}>Exclusions</span>
        <p className={classnames('text-light8 mt-3')}>
          You can select providers you would like to exclude from your DRM list
          or you can add them manually.
        </p>
        <div className="mt-5">
          <span className={styles.textForm}>
            How would you like to exclude providers
          </span>
          <div className="mt-3">
            <GeneralSelect
              options={options}
              handleChange={handleChangeExcludeMethod}
              placeHolder="Select an Option"
              selectedOption={selectedOption}
              fullWidth
            />
          </div>
        </div>
        {selectedOption?.value === SEARCH_FILTER_KEYS.exclude_providers &&
          existsFilter(SEARCH_FILTER_KEYS.exclude_providers) && (
            <ExcludeProvidersFilter
              filters={filters}
              control={control}
              errors={errors}
            />
          )}
        {selectedOption?.value === SEARCH_FILTER_KEYS.exclude_providers_drm &&
          existsFilter(SEARCH_FILTER_KEYS.exclude_providers_drm) && (
            <ExcludeDataVendorsFilter control={control} />
          )}
      </div>
      <ConfirmationModal
        centered={true}
        animation={true}
        show={showModal}
        onCancel={onCancelModal}
        okBtnText="Yes, Proceed"
        onOk={onConfirmModal}
        title={'Are you sure?'}
      >
        <div>
          <p>
            If you don’t exclude any providers you run the risk of receiving a
            less than optimal response.
          </p>
          <p>
            <b>
              Are you sure you want to proceed without excluding any providers?
            </b>
          </p>
        </div>
      </ConfirmationModal>
    </div>
  );
};

export default ProvidersStep;
